/**
 * Error classes for the SDK
 */

/**
 * Base error class for all SDK errors
 */
export class FeedbackError extends Error {
  /** Error code */
  code: string;
  /** HTTP status code (if applicable) */
  statusCode?: number;
  /** Additional error details */
  details?: any;
  /** Backend request id, if provided */
  requestId?: string;

  constructor(message: string, code: string = "UNKNOWN_ERROR") {
    super(message);
    this.name = "FeedbackError";
    this.code = code;

    // Maintains proper stack trace for where error was thrown
    if ((Error as any).captureStackTrace) {
      (Error as any).captureStackTrace(this, this.constructor);
    }
  }
}

/**
 * Validation error (400)
 */
export class ValidationError extends FeedbackError {
  constructor(message: string, details?: any) {
    super(message, "VALIDATION_ERROR");
    this.name = "ValidationError";
    this.statusCode = 400;
    this.details = details;
  }
}

/**
 * Authentication error (401)
 */
export class AuthenticationError extends FeedbackError {
  constructor(message: string = "Authentication failed") {
    super(message, "AUTHENTICATION_ERROR");
    this.name = "AuthenticationError";
    this.statusCode = 401;
  }
}

/**
 * Authorization error (403)
 */
export class AuthorizationError extends FeedbackError {
  constructor(message: string = "Access denied") {
    super(message, "AUTHORIZATION_ERROR");
    this.name = "AuthorizationError";
    this.statusCode = 403;
  }
}

/**
 * Not found error (404)
 */
export class NotFoundError extends FeedbackError {
  constructor(message: string = "Resource not found") {
    super(message, "NOT_FOUND");
    this.name = "NotFoundError";
    this.statusCode = 404;
  }
}

/**
 * Rate limit error (429)
 */
export class RateLimitError extends FeedbackError {
  constructor(message: string = "Rate limit exceeded", retryAfter?: number) {
    super(message, "RATE_LIMIT_ERROR");
    this.name = "RateLimitError";
    this.statusCode = 429;
    this.details = retryAfter ? { retryAfter } : undefined;
  }
}

/**
 * Network error (connection failed, timeout, etc.)
 */
export class NetworkError extends FeedbackError {
  constructor(message: string = "Network request failed") {
    super(message, "NETWORK_ERROR");
    this.name = "NetworkError";
  }
}

/**
 * Server error (5xx)
 */
export class ServerError extends FeedbackError {
  constructor(message: string = "Server error", statusCode: number = 500) {
    super(message, "SERVER_ERROR");
    this.name = "ServerError";
    this.statusCode = statusCode;
  }
}

/**
 * Parse error response from API
 */
export function parseErrorResponse(
  response: Response,
  data: any
): FeedbackError {
  const errObj = data?.error;
  const message = errObj?.message || data?.message || "An error occurred";
  const code = errObj?.code || data?.code;
  const details = errObj?.details ?? data?.details;
  const requestId = data?.request_id || data?.requestId;

  switch (response.status) {
    case 400:
      return Object.assign(new ValidationError(message, details), {
        code: code ?? "BAD_REQUEST",
        requestId,
      });
    case 422:
      return Object.assign(new ValidationError(message, details), {
        code: code ?? "UNPROCESSABLE_ENTITY",
        requestId,
      });
    case 401:
      return Object.assign(new AuthenticationError(message), {
        code: code ?? "UNAUTHORIZED",
        requestId,
      });
    case 403:
      return Object.assign(new AuthorizationError(message), {
        code: code ?? "FORBIDDEN",
        requestId,
      });
    case 404:
      return Object.assign(new NotFoundError(message), {
        code: code ?? "NOT_FOUND",
        requestId,
      });
    case 429:
      const retryAfter = response.headers.get("Retry-After");
      return Object.assign(
        new RateLimitError(
          message,
          retryAfter ? parseInt(retryAfter) : undefined
        ),
        { code: code ?? "TOO_MANY_REQUESTS", requestId }
      );
    case 500:
    case 502:
    case 503:
    case 504:
      return Object.assign(new ServerError(message, response.status), {
        code: code ?? "SERVER_ERROR",
        requestId,
      });
    default:
      const error = new FeedbackError(message, code);
      error.statusCode = response.status;
      error.details = details;
      error.requestId = requestId;
      return error;
  }
}

/**
 * Check if error is retryable
 */
export function isRetryableError(error: any): boolean {
  // Network errors
  if (error instanceof NetworkError) {
    return true;
  }

  // Server errors (5xx)
  if (error instanceof ServerError) {
    return true;
  }

  // Rate limit errors
  if (error instanceof RateLimitError) {
    return true;
  }

  // Check status code
  if (error.statusCode) {
    // 5xx errors
    if (error.statusCode >= 500 && error.statusCode < 600) {
      return true;
    }
    // 429 rate limit
    if (error.statusCode === 429) {
      return true;
    }
  }

  return false;
}
